/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */
import type {
  CustomerInfo,
  InvoiceDraftRequested,
  InvoiceFinancials,
  Order,
  OrderItem,
  RepositoryError,
} from "@invoiceddd/domain";
import { Context, Data, Effect, Layer } from "effect";
import { OrderRepository } from "../ports/OrderRepository";
import { EventPublisher } from "../services/EventPublisher";

/**
 * Use case errors
 */
export class OrderNotFoundError extends Data.TaggedError("@/OrderNotFoundError")<{
  readonly cause?: unknown;
  readonly message: string;
  readonly orderId: string;
}> {}

export class InvoiceDraftCreationError extends Data.TaggedError("@/InvoiceDraftCreationError")<{
  readonly cause?: unknown;
  readonly message: string;
  readonly orderId: string;
}> {}

/**
 * Invoice draft data structure containing all information needed to create an invoice
 */
export interface InvoiceDraft {
  readonly orderId: string;
  readonly customerSnapshot: CustomerInfo;
  readonly itemsSnapshot: readonly OrderItem[];
  readonly financials: InvoiceFinancials;
  readonly requestedBy: string;
  readonly requestedAt: Date;
}

/**
 * Input for requesting an invoice draft
 */
export interface RequestInvoiceInput {
  readonly orderId: string;
  readonly requestedBy: string;
}

/**
 * Use case for requesting an invoice draft from an existing order.
 * This use case validates that the order exists and transforms order data
 * into an editable invoice draft format.
 */
export interface RequestInvoiceUseCaseSchema {
  /**
   * Request an invoice draft from an order
   * @param input - Order ID and user information
   * @returns Effect containing invoice draft or error
   */
  readonly execute: (
    input: RequestInvoiceInput
  ) => Effect.Effect<
    InvoiceDraft,
    OrderNotFoundError | InvoiceDraftCreationError | RepositoryError
  >;
}

/**
 * Context tag for RequestInvoiceUseCase
 */
export class RequestInvoiceUseCase extends Context.Tag("@/UseCases/RequestInvoiceUseCase")<
  RequestInvoiceUseCase,
  RequestInvoiceUseCaseSchema
>() {}

/**
 * Implementation of RequestInvoiceUseCase
 */
export const RequestInvoiceUseCaseLive = Layer.effect(
  RequestInvoiceUseCase,
  Effect.gen(function* () {
    const orderRepository = yield* OrderRepository;
    const eventPublisher = yield* EventPublisher;

    const implementation: RequestInvoiceUseCaseSchema = {
      execute: (input: RequestInvoiceInput) => {
        return Effect.gen(function* () {
          // Find the order
          const orderOption = yield* orderRepository.findById(input.orderId);

          // Check if order exists
          if (orderOption._tag === "None") {
            return yield* Effect.fail(
              new OrderNotFoundError({
                message: `Order with ID ${input.orderId} not found`,
                orderId: input.orderId,
              })
            );
          }

          const order = orderOption.value;

          // Transform order data to invoice draft
          const requestedAt = new Date();
          const invoiceDraft = yield* createInvoiceDraftFromOrder(
            order,
            input.requestedBy,
            requestedAt
          );

          // Publish domain event
          const event: InvoiceDraftRequested = {
            type: "InvoiceDraftRequested",
            eventId: crypto.randomUUID(),
            occurredOn: requestedAt,
            version: 1,
            aggregateType: "Invoice",
            aggregateId: input.orderId, // Use orderId as aggregate ID for draft
            correlationId: crypto.randomUUID(),
            orderId: input.orderId,
            requestedBy: input.requestedBy,
            requestedAt,
            orderSnapshot: {
              customerInfo: serializeCustomerInfo(order.customer),
              items: order.items.map(serializeOrderItem),
              financials: serializeFinancials(order),
            },
          };

          yield* eventPublisher.publish(event);

          return invoiceDraft;
        }).pipe(
          Effect.mapError(error => {
            if (error._tag === "@/OrderNotFoundError") {
              return error;
            }
            return new InvoiceDraftCreationError({
              message: `Failed to create invoice draft: ${String(error)}`,
              orderId: input.orderId,
              cause: error,
            });
          }),
          Effect.withSpan("UseCases.RequestInvoice.execute", {
            attributes: {
              orderId: input.orderId,
              requestedBy: input.requestedBy,
            },
          })
        );
      },
    };

    return RequestInvoiceUseCase.of(implementation);
  })
);

/**
 * Transform order data into an invoice draft
 */
const createInvoiceDraftFromOrder = (
  order: Order,
  requestedBy: string,
  requestedAt: Date
): Effect.Effect<InvoiceDraft, InvoiceDraftCreationError> => {
  return Effect.gen(function* () {
    // Create snapshots of order data at request time
    const customerSnapshot: CustomerInfo = {
      firstName: order.customer.firstName,
      lastName: order.customer.lastName,
      street: order.customer.street,
      zip: order.customer.zip,
      city: order.customer.city,
      country: order.customer.country,
      email: order.customer.email,
      phone: order.customer.phone,
    };

    const itemsSnapshot: readonly OrderItem[] = order.items.map(item => ({
      name: item.name,
      productNumber: item.productNumber,
      variant: item.variant,
      unitPrice: item.unitPrice,
      quantity: item.quantity,
      linePrice: item.linePrice,
    }));

    const financials: InvoiceFinancials = {
      subtotal: order.subtotal,
      taxes: order.taxes,
      shippingCost: order.shippingCost,
      discount: order.discount,
      grandTotal: order.grandTotal,
    };

    yield* Effect.annotateCurrentSpan({
      finished: true,
    });

    return {
      orderId: order.orderId,
      customerSnapshot,
      itemsSnapshot,
      financials,
      requestedBy,
      requestedAt,
    };
  }).pipe(
    Effect.mapError(error => {
      return new InvoiceDraftCreationError({
        message: `Failed to transform order to invoice draft: ${error}`,
        orderId: order.orderId,
        cause: error,
      });
    }),
    Effect.withSpan("UseCases.RequestInvoice.createInvoiceDraftFromOrder", {
      attributes: {
        orderId: order.orderId,
        requestedBy,
        requestedAt,
      },
    })
  );
};

/**
 * Serialize customer info for event payload
 */
const serializeCustomerInfo = (customer: CustomerInfo): Record<string, unknown> => ({
  firstName: customer.firstName,
  lastName: customer.lastName,
  street: customer.street,
  zip: customer.zip,
  city: customer.city,
  country: customer.country,
  email: customer.email,
  phone: customer.phone,
});

/**
 * Serialize order item for event payload
 */
const serializeOrderItem = (item: OrderItem): Record<string, unknown> => ({
  name: item.name,
  productNumber: item.productNumber,
  variant: item.variant,
  unitPrice: {
    amount: item.unitPrice.amount,
    currency: item.unitPrice.currency,
  },
  quantity: item.quantity,
  linePrice: {
    amount: item.linePrice.amount,
    currency: item.linePrice.currency,
  },
});

/**
 * Serialize financials for event payload
 */
const serializeFinancials = (order: Order): Record<string, unknown> => ({
  subtotal: {
    amount: order.subtotal.amount,
    currency: order.subtotal.currency,
  },
  taxes: {
    amount: order.taxes.amount,
    currency: order.taxes.currency,
  },
  shippingCost: {
    amount: order.shippingCost.amount,
    currency: order.shippingCost.currency,
  },
  discount: {
    amount: order.discount.amount,
    currency: order.discount.currency,
  },
  grandTotal: {
    amount: order.grandTotal.amount,
    currency: order.grandTotal.currency,
  },
});
